import React from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import { useIntl } from 'react-intl';
import { IconButton } from '../design-system';

interface ImageDeleteButtonProps {
  onDelete: () => void;
  title?: string;
  disabled?: boolean;
  /** Distance from the top right corner of the thumbnail, in pixels. */
  offset?: number;
}

const IMAGE_DELETE_BUTTON_CLASS = 'image-delete-button';

/**
 * Styles for the thumbnail wrapper that reveal the delete button on hover.
 * Touch devices have no hover state, so there the button stays visible.
 */
export const imageDeleteRevealStyles = {
  position: 'relative',
  [`& .${IMAGE_DELETE_BUTTON_CLASS}`]: {
    opacity: 0,
    pointerEvents: 'none',
  },
  [`&:hover .${IMAGE_DELETE_BUTTON_CLASS}, & .${IMAGE_DELETE_BUTTON_CLASS}:focus-visible`]: {
    opacity: 1,
    pointerEvents: 'auto',
  },
  '@media (hover: none)': {
    [`& .${IMAGE_DELETE_BUTTON_CLASS}`]: {
      opacity: 1,
      pointerEvents: 'auto',
    },
  },
};

const buttonStyles = {
  position: 'absolute',
};
const getPositionStyles = (offset: number) => ({ top: offset, right: offset });
const handleMouseDown = (event: React.MouseEvent<HTMLButtonElement>) => {
  event.stopPropagation();
};

const ImageDeleteButton = ({
  onDelete,
  title,
  disabled = false,
  offset = 4,
}: ImageDeleteButtonProps) => {
  const intl = useIntl();
  const buttonTitle =
    title ||
    intl.formatMessage({
      id: 'image.delete.action',
      defaultMessage: 'Delete image',
    });
  const positionStyles = getPositionStyles(offset);
  const combinedStyles = { ...buttonStyles, ...positionStyles };
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    // Keeps the click from reaching the thumbnail underneath, which would
    // otherwise open the lightbox or the file dialog.
    event.preventDefault();
    event.stopPropagation();
    onDelete();
  };

  return (
    <IconButton
      className={IMAGE_DELETE_BUTTON_CLASS}
      aria-label={buttonTitle}
      title={buttonTitle}
      density="compact"
      disabled={disabled}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      sx={combinedStyles}
      tone="danger"
      variant="overlay"
    >
      <DeleteIcon />
    </IconButton>
  );
};

export { ImageDeleteButton };
